// Layer 4 (UI): the Senior metallurgist landing's view pieces (polish.P27
// #714, locked comp `.impeccable/mocks/decision/landing-senior-met.html` A):
// the area blocks and compound cause, the cascade list and the KB suggestion
// rows. Every figure comes from `SeniorMetTabs.transform.ts` /
// `SeniorMetLanding.transform.ts`; this file only draws them.

import { Fragment, type ReactNode } from 'react';
import { cn } from '@/shared/cn';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { NEUTRAL_PILL } from './LandingDecisionGate';
import type { CascadeRow, KbSuggestion, ProcessHealthInsight } from './SeniorMetTabs.transform';
import type { SmetAreaBlock } from './SeniorMetLanding.transform';

const PILL_BASE =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

/** Status pill on the state tokens (comp `.pill`). State colour marks state
 *  only; an in-band `ok` is quiet unless `okColour` is asked for (the KB
 *  lifecycle pills, comp adjudication 6). */
export function statePill(tone: 'ok' | 'warn' | 'bad' | 'neutral', okColour = false): string {
  switch (tone) {
    case 'ok':
      return okColour
        ? cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-online)_12%,transparent)] text-[var(--state-online)]')
        : NEUTRAL_PILL;
    case 'warn':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]');
    case 'bad':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)]');
    default:
      return NEUTRAL_PILL;
  }
}

/** Outlined neutral chip (comp `.chip`): an area or a category, never a state. */
export const NEUTRAL_CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-[7px] text-[10px] font-semibold uppercase leading-4 tracking-[0.05em] text-ink-secondary';

/** Section head inside a card (comp `.sec`). */
export function SectionHead({ children, meta, className }: { children: ReactNode; meta?: ReactNode; className?: string }) {
  return (
    <p className={cn('mb-0.5 mt-2.5 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted first:mt-0', className)}>
      <span>{children}</span>
      {meta ? <span className="ml-auto text-[11.5px] font-medium normal-case tracking-normal">{meta}</span> : null}
    </p>
  );
}

// ---- Process health ------------------------------------------------------------

/** The five plant areas as blocks (comp `.area`): state pill, area, the short
 *  read, then the readings four across, each against its comparison. */
export function AreaBlocks({ blocks }: { blocks: readonly SmetAreaBlock[] }) {
  return (
    <div data-smet-areas>
      {blocks.map((block) => (
        <div key={block.id} className="border-b border-[var(--color-divider)] pb-[7px] pt-1.5" data-smet-area={block.id}>
          <div className="mb-[3px] flex items-center gap-2">
            <span className={statePill(block.pill.tone)} data-smet-area-state={block.pill.tone}>
              {block.pill.label}
            </span>
            <span className="text-[13px] font-semibold text-ink-primary">{block.area}</span>
            {block.tag ? <span className="text-[11.5px] text-muted">· {block.tag}</span> : null}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums sm:grid-cols-4">
            {block.rows.map((row) => (
              <div key={row.metric} className="flex min-w-0 flex-col" data-smet-reading={row.metric}>
                <span className="truncate text-[11.5px] text-muted">{row.metric}</span>
                <span className="text-[12.5px]">
                  <span
                    className="font-semibold text-ink-primary"
                    style={row.tone === 'ok' || row.tone === 'neutral' ? undefined : { color: vizToneColor(row.tone) }}
                  >
                    {row.value}
                  </span>
                  {row.vsTarget ? (
                    <>
                      {' '}
                      <span className="whitespace-nowrap text-[11.5px] text-muted">{row.vsTarget}</span>
                    </>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The compound cause (adjudication 8): an Engine-chip insight on the card
 *  surface, closing on the fix sequence as numbered steps. */
export function CompoundInsight({ insight }: { insight: ProcessHealthInsight }) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1 text-[13px]" data-smet-compound>
      <ActorChip actor="Engine" />
      <div className="min-w-0">
        <p className="font-semibold text-ink-primary">{insight.headline}</p>
        <p className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary">
          Consistent with the observed {insight.gapText} · fix sequence
        </p>
        {/* The three steps hold together on their own line (comp A). */}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12.5px] tabular-nums" data-smet-fix-steps>
          {insight.steps.map((step) => (
            <span
              key={step.n}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[9px] border border-[var(--hairline-strong)] bg-[var(--surface-2)] py-0.5 pl-1 pr-2 text-ink-primary"
              data-smet-fix-step={step.n}
            >
              <span className="grid size-[18px] place-items-center rounded-full bg-[var(--hairline)] text-[11px] font-semibold text-ink-secondary">
                {step.n}
              </span>
              {step.label} <span className="text-muted">{step.time}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- 7-day trend analysis ----------------------------------------------------------

/** One sentence with its turning figure set in weight, once. */
function Emphasised({ text, strong }: { text: string; strong?: string }) {
  if (!strong) return <>{text}</>;
  const at = text.indexOf(strong);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-semibold text-ink-primary">{strong}</span>
      {text.slice(at + strong.length)}
    </>
  );
}

/** The upstream-to-downstream cascade (comp `.casc`): the chart's numeral, the
 *  day with its state dot, then the step (adjudication 5). */
export function CascadeList({ rows }: { rows: readonly CascadeRow[] }) {
  return (
    <ol data-smet-cascade>
      {rows.map((row, index) => (
        <li
          key={row.id}
          className="grid grid-cols-[22px_118px_minmax(0,1fr)] items-start gap-x-2.5 border-b border-[var(--color-divider)] py-2 text-[12.5px] tabular-nums last:border-b-0"
          data-smet-cascade-step={index + 1}
        >
          <span className="mt-px grid size-[18px] place-items-center rounded-full border border-[var(--hairline-strong)] text-[10.5px] font-semibold text-ink-secondary">
            {index + 1}
          </span>
          <span className="flex items-center whitespace-nowrap font-semibold text-ink-primary">
            <span className="mr-1.5 inline-block size-[7px] rounded-full" style={{ backgroundColor: vizToneColor(row.tone) }} aria-hidden="true" />
            {row.label}
          </span>
          <span className="text-ink-secondary">
            <Emphasised text={row.text} strong={row.strong} />
          </span>
        </li>
      ))}
    </ol>
  );
}

// ---- KB update suggestions --------------------------------------------------------

/** Status pill per lifecycle state (comp `.kb .st`): the state keeps its
 *  colour here, on the pill only; confidence stays neutral (adjudication 6). */
function kbStatusPill(tone: KbSuggestion['statusTone']): string {
  return statePill(tone, true);
}

/** KB update suggestions (comp `.kb`): type, title, evidence and status, with
 *  the confidence as a neutral figure and bar at the right. */
export function KbSuggestionList({ suggestions }: { suggestions: readonly KbSuggestion[] }) {
  return (
    <div data-smet-kb>
      {suggestions.map((row) => (
        <div
          key={row.id}
          className="grid grid-cols-[minmax(0,1fr)_92px] gap-x-4 gap-y-0.5 border-b border-[var(--color-divider)] py-2.5 last:border-b-0"
          data-smet-kb-row={row.id}
        >
          <div className="min-w-0">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted">{row.type}</p>
            <p className="text-[13px] font-semibold text-ink-primary">{row.title}</p>
            <p className="mt-0.5 text-[12.5px] text-ink-secondary">{row.evidence}</p>
            <p className="mt-[5px]">
              <span className={kbStatusPill(row.statusTone)}>{row.status}</span>
            </p>
          </div>
          <div className="text-right tabular-nums" data-smet-kb-confidence={row.confidencePct}>
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted">Confidence</p>
            <p className="text-[15px] font-semibold text-ink-primary">{row.confidencePct}%</p>
            <span className="mt-[5px] block h-1 overflow-hidden rounded-full bg-[var(--hairline-strong)]" aria-hidden="true">
              <span className="block h-full rounded-full bg-[var(--ink-secondary)]" style={{ width: `${row.confidencePct}%` }} />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** `Label · value` pairs split by hairlines (comp `.kv`). */
export function KvRows({ rows }: { rows: readonly { id: string; left: ReactNode; right: ReactNode }[] }) {
  return (
    <div>
      {rows.map((row) => (
        <Fragment key={row.id}>
          <div className="flex items-baseline justify-between gap-2.5 border-b border-[var(--color-divider)] py-2 text-[12.5px] tabular-nums last:border-b-0">
            <span className="min-w-0 text-ink-secondary">{row.left}</span>
            <span className="shrink-0">{row.right}</span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}
